import * as React from "react";
import * as SharedState from "../../../../../shared-state";
import * as Load from "../load";
import * as SettingsOverride from "../../../../components/settings-overrides";
import styled from "styled-components";
import { Action } from "../state";
import { Dispatch } from "@typescript-tea/core";
import { PropertyValueSet } from "@genesys/property";
import { Props } from "../types";
import { MoistureHeatLoadTypeEnum } from "@genesys/graphql-types";
import { ClickAwayListener } from "@mui/material";
import { PropertyDefinitions } from "../../../../tools";
import { BorderRadius } from "@genesys/ui-elements";

const MultiLoadViewContainer = styled.div<{ readonly isSelected: boolean }>`
  border-radius: ${BorderRadius.standard};
  border: 2.5px solid ${props => (props.isSelected ? "black" : "transparent")};
  margin-right: 15px;
`;

export const MultiLoadView = (props: Props) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const {
    state,
    isDisabled,
    loadDef,
    sharedState,
    selectedLoadGroup,
    handleSelectLoadGroup,
    dispatch
  } = props;

  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const activeLoadStates = Object.values(state.activeLoads);

  const selectedLoad = state.selectedLoadId
    ? state.activeLoads[state.selectedLoadId]
    : activeLoadStates[0];

  const selectedLoadInActiveGroup =
    loadDef.sourceName === selectedLoadGroup ? selectedLoad : undefined;

  const onClickaway = (e: any) => {
    if (
      (e.target as any).nodeName !== "BODY" &&
      selectedLoadGroup &&
      handleSelectLoadGroup !== undefined
    ) {
      handleSelectLoadGroup(undefined);
    } else {
      return;
    }
  };

  const createLoadContent = (load: Load.ActiveLoadState) => {
    const loads = activeLoadStates.filter(x => x.id !== load.id);
    return (
      <LoadContainer
        isLoadNameDuplicated={loads.map(x => x.name).includes(load.name)}
        key={load.id}
        load={load}
        loadDef={loadDef}
        propertyDefinitions={state.propertyDefinitions}
        currentTopLevelCaseOverrides={props.currentTopLevelCaseOverrides}
        currentTopLevelMoistureLoadSettings={
          props.currentTopLevelMoistureLoadSettings
        }
        sharedState={sharedState}
        selectedLoad={selectedLoad}
        selectedLoadGroup={selectedLoadGroup}
        selectedLoadInActiveGroup={selectedLoadInActiveGroup}
        isDisabled={isDisabled}
        handleSelectLoadGroup={handleSelectLoadGroup}
        dispatch={dispatch}
      />
    );
  };

  return activeLoadStates.map(load =>
    selectedLoadInActiveGroup === load ? (
      <ClickAwayListener
        onClickAway={onClickaway}
        mouseEvent="onClick"
        key={load.id}
      >
        <div ref={scrollRef}>{createLoadContent(load)}</div>
      </ClickAwayListener>
    ) : (
      createLoadContent(load)
    )
  );
};

const LoadContainer = ({
  load,
  loadDef,
  propertyDefinitions,
  currentTopLevelCaseOverrides,
  isLoadNameDuplicated,
  currentTopLevelMoistureLoadSettings,
  sharedState,
  selectedLoad,
  selectedLoadGroup,
  selectedLoadInActiveGroup,
  isDisabled,
  handleSelectLoadGroup,
  dispatch
}: {
  readonly load: Load.ActiveLoadState;
  readonly isLoadNameDuplicated: boolean;
  readonly loadDef: Load.LoadDef;
  readonly propertyDefinitions: PropertyDefinitions;
  readonly currentTopLevelCaseOverrides: SettingsOverride.SettingsOverrides;
  readonly currentTopLevelMoistureLoadSettings: PropertyValueSet.PropertyValueSet;
  readonly sharedState: SharedState.State;
  readonly selectedLoad: Load.ActiveLoadState | undefined;
  readonly selectedLoadGroup: string | undefined;
  readonly selectedLoadInActiveGroup: Load.ActiveLoadState | undefined;
  readonly isDisabled: boolean;
  readonly handleSelectLoadGroup:
    | ((group: MoistureHeatLoadTypeEnum | undefined) => void)
    | undefined;
  readonly dispatch: Dispatch<Action>;
}) => {
  const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (selectedLoad !== load || loadDef.sourceName !== selectedLoadGroup) {
      if (
        loadDef.sourceName !== selectedLoadGroup &&
        handleSelectLoadGroup !== undefined
      ) {
        handleSelectLoadGroup(loadDef.sourceName);
      }
      dispatch(Action.setSelectedLoad(load.id));
    }
    e.stopPropagation();
  };

  return (
    <MultiLoadViewContainer
      isSelected={selectedLoadInActiveGroup === load}
      onClick={handleClick}
    >
      <Load.View
        isLoadNameDuplicated={isLoadNameDuplicated}
        headerType="multi"
        state={load}
        sharedState={sharedState}
        currentTopLevelCaseOverrides={currentTopLevelCaseOverrides}
        propertyDefinitions={propertyDefinitions}
        loadDef={loadDef}
        isDisabled={isDisabled}
        currentTopLevelMoistureLoadSettings={
          currentTopLevelMoistureLoadSettings
        }
        dispatch={Dispatch.map(
          action => Action.dispatchLoad(load.id, action, loadDef),
          dispatch
        )}
      />
    </MultiLoadViewContainer>
  );
};
